import LottoShell from "../components/lotto/LottoShell";
import {
  Box,
  Typography,
  Button,
  Stack,
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import LockIcon from "@mui/icons-material/Lock";
import SecurityIcon from "@mui/icons-material/Security";
import LogoutIcon from "@mui/icons-material/Logout";
import QrCodeIcon from "@mui/icons-material/QrCode";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import HistoryIcon from "@mui/icons-material/History";
import { useAuth } from "../AuthProvider";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import toastStyles from "../utils/styles";
import { getApiErrorMessage } from "../utils/apiError";
import { createTransaction, CreateTransactionParams } from "../api";
import LoadingOverlay from "../components/LoadingOverlay";
import i18next from "i18next";
import { PATTERNS, MESSAGES, secureString } from "../utils/validation";
import { useSecureInput } from "../hooks/useSecureInput";
import { useCustomColors } from "../hooks/useCustomColors";

// Validation schema
const paymentSchema = Yup.object().shape({
  amount: Yup.string()
    .required("Amount is required")
    .matches(
      /^\d+(\.\d{0,2})?$/,
      "Amount must be a valid number with up to 2 decimal places"
    )
    .test("min-amount", "Minimum amount is MYR 1.00", (value) => {
      const num = parseFloat(value || "0");
      return num >= 1;
    })
    .test("positive-amount", "Amount must be positive", (value) => {
      const num = parseFloat(value || "0");
      return num > 0;
    }),

  remarks: secureString((key, options) => i18next.t(key, options), { maxLength: 200 })
    .matches(PATTERNS.remarks, MESSAGES.remarks),

  paymentMethod: Yup.string()
    .required("Payment method is required")
    .oneOf(
      ["bank_transfer", "duitnow"],
      "Please select a valid payment method"
    ),
});

const PaymentPage = () => {
  const colors = useCustomColors();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(paymentSchema),
    defaultValues: {
      paymentMethod: "bank_transfer",
      remarks: "",
    },
  });

  const watchedValues = watch();
  const remarks = useSecureInput({ name: "remarks", control, maxLength: 200 });
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Inject custom toast styles
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = toastStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Logged out successfully!");
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Failed to logout. Please try again.");
    }
  };

  // The schema's finalised values (trimmed, normalised), not the raw inputs.
  const submitted = useRef<{ amount: string; remarks?: string; paymentMethod: string }>();

  const onSubmit = (data: { amount: string; remarks?: string; paymentMethod: string }) => {
    submitted.current = data;
    setShowConfirmModal(true);
  };

  const handleConfirmPayment = async () => {
    const data = submitted.current;
    if (!data) return;
    setIsLoading(true);
    setShowConfirmModal(false);

    try {
      const transactionParams: CreateTransactionParams = {
        amount: parseFloat(data.amount),
        payment_method: data.paymentMethod as "bank_transfer" | "duitnow",
        remarks: data.remarks || undefined,
      };

      const response = await createTransaction(transactionParams);

      if (response.status === 1) {
        toast.success("Transaction created successfully!");
        // Navigate to bank info page
        navigate(`/bank-info/${response.data.transactionId}`);
      } else {
        toast.error(
          response.message || "Failed to create transaction. Please try again."
        );
      }
    } catch (err) {
      console.error("Transaction error:", err);
      toast.error(getApiErrorMessage(err, "Failed to create transaction. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelPayment = () => {
    setShowConfirmModal(false);
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case "bank_transfer":
        return <AccountBalanceIcon sx={{ fontSize: "20px", mr: 1, color: colors.accent.gold }} />;
      case "duitnow":
        return <QrCodeIcon sx={{ fontSize: "20px", mr: 1, color: colors.accent.gold }} />;
      default:
        return <AccountBalanceIcon sx={{ fontSize: "20px", mr: 1, color: colors.accent.gold }} />;
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case "bank_transfer":
        return "Bank Transfer";
      case "duitnow":
        return "DuitNow";
      default:
        return method;
    }
  };

  const renderConfirmationModal = () => (
    <Dialog
      open={showConfirmModal}
      onClose={handleCancelPayment}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.background.page,
          color: colors.isDark ? "#FFFFFF" : colors.text.primary,
          borderRadius: { xs: "12px", sm: "16px" },
          border: `1px solid ${colors.border.accent}`,
          mx: { xs: 2, sm: 0 },
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", pb: { xs: 1, sm: 2 }, px: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            fontSize: { xs: "36px", sm: "48px" },
            mb: { xs: 1, sm: 2 },
            textAlign: "center",
          }}
        >
          ✨
        </Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "1.4rem", sm: "1.8rem", md: "2rem" },
            background: colors.gradient.gold,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            color: "transparent",
            lineHeight: { xs: 1.2, sm: 1.3 },
          }}
        >
          Confirm Your Payment Details
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
        <Stack spacing={{ xs: 2, sm: 3 }}>
          {/* Payment Amount */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: colors.text.secondary,
                fontSize: { xs: "11px", sm: "12px" },
                fontWeight: "bold",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                mb: 1,
                display: "block",
              }}
            >
              Payment Amount
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: colors.accent.gold,
                fontSize: { xs: "20px", sm: "24px", md: "28px" },
                fontWeight: "bold",
                backgroundColor: colors.card.background,
                padding: { xs: "10px 14px", sm: "12px 16px" },
                borderRadius: "8px",
                border: `1px solid ${colors.border.accent}`,
                textAlign: "center",
                wordBreak: "break-word",
              }}
            >
              MYR {watchedValues.amount}
            </Typography>
          </Box>

          {/* Payment Method */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: colors.text.secondary,
                fontSize: { xs: "11px", sm: "12px" },
                fontWeight: "bold",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                mb: 1,
                display: "block",
              }}
            >
              Payment Method
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "center", sm: "flex-start" },
                backgroundColor: colors.card.background,
                padding: { xs: "10px 14px", sm: "12px 16px" },
                borderRadius: "8px",
                border: `1px solid ${colors.border.accent}`,
                gap: 1,
              }}
            >
              {getPaymentMethodIcon(watchedValues.paymentMethod)}
              <Typography
                variant="body1"
                sx={{
                  color: colors.isDark ? "#FFFFFF" : colors.text.primary,
                  fontSize: { xs: "14px", sm: "16px" },
                  fontWeight: "500",
                }}
              >
                {getPaymentMethodLabel(watchedValues.paymentMethod)}
              </Typography>
            </Box>
          </Box>

          {/* Remarks */}
          {watchedValues.remarks && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: colors.text.secondary,
                  fontSize: { xs: "11px", sm: "12px" },
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: 1,
                  display: "block",
                }}
              >
                Remarks
              </Typography>
              <Box
                sx={{
                  backgroundColor: colors.card.background,
                  padding: { xs: "10px 14px", sm: "12px 16px" },
                  borderRadius: "8px",
                  border: `1px solid ${colors.border.accent}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.isDark ? "#FFFFFF" : colors.text.primary,
                    fontSize: { xs: "13px", sm: "14px" },
                  }}
                >
                  {watchedValues.remarks}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Info */}
          <Box
            sx={{
              backgroundColor: colors.tint.primary,
              border: `1px solid ${colors.tint.goldStrong}`,
              borderRadius: "8px",
              padding: { xs: "12px", sm: "16px" },
              display: "flex",
              alignItems: "flex-start",
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography sx={{
              fontSize: { xs: "18px", sm: "20px" },
              color: colors.accent.gold,
              flexShrink: 0,
            }}>
              💡
            </Typography>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: colors.isDark ? "#FFFFFF" : colors.text.primary,
                  fontSize: { xs: "12px", sm: "14px" },
                  fontWeight: "500",
                  mb: 0.5,
                  lineHeight: { xs: 1.4, sm: 1.5 },
                }}
              >
                After confirming, you will be shown bank transfer details.
                Please transfer the exact amount and upload your receipt.
              </Typography>
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{
        justifyContent: "center",
        gap: { xs: 1, sm: 2 },
        mt: { xs: 1, sm: 2 },
        p: { xs: 2, sm: 4 },
        flexDirection: { xs: "column", sm: "row" },
      }}>
        <Button
          onClick={handleCancelPayment}
          variant="outlinedDark"
          sx={{
            flex: { xs: "none", sm: 1 },
            width: { xs: "100%", sm: "auto" },
            minHeight: { xs: "48px", sm: "56px", md: "64px" },
            fontSize: { xs: "14px", sm: "16px" },
          }}
        >
          Review
        </Button>

        <Button
          onClick={handleConfirmPayment}
          disabled={isLoading}
          sx={{
            flex: { xs: "none", sm: 1 },
            width: { xs: "100%", sm: "auto" },
            minHeight: { xs: "48px", sm: "56px", md: "64px" },
            fontSize: { xs: "14px", sm: "16px" },
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} sx={{ color: "#FFFFFF" }} />
          ) : (
            "Confirm & Proceed"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <LottoShell>
      <Box
        sx={{
          minHeight: "100vh",
          background:
            `radial-gradient(circle at center, ${colors.card.background} 0%, ${colors.background.page} 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
          px: 2,
        }}
      >
        <Card
          sx={{
            maxWidth: 480,
            width: "100%",
            boxShadow:
              "0 20px 60px rgba(0, 0, 0, 0.3), 0 0 1px rgba(255, 255, 255, 0.1)",
            border: `1px solid ${colors.background.panelSecondary}`,
            background: colors.isDark ? "#ffffff08" : colors.card.background,
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderRadius: "16px",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ mb: 4, textAlign: "center" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "center",
                  mb: 1,
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    background:
                      colors.gradient.gold,
                    boxShadow: `0 4px 20px ${colors.border.accent}`,
                    borderRadius: "8px",
                    mr: { xs: 0, md: 2 },
                    mb: { xs: 2, md: 0 },
                    textAlign: "center",
                  }}
                />
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                    background:
                      colors.gradient.gold,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    color: "transparent",
                  }}
                >
                  eclwin88
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ color: colors.text.secondary, fontSize: "14px" }}
              >
                Secure Payment Gateway
              </Typography>
            </Box>
            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text.secondary,
                    fontSize: "12px",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    mb: 1,
                    display: "block",
                  }}
                >
                  Amount (MYR)
                </Typography>
                <TextField
                  fullWidth
                  type="text"
                  inputMode="decimal"
                  {...register("amount")}
                  placeholder="0.00"
                  error={!!errors.amount}
                  helperText={errors.amount?.message}
                  onInput={(e) => {
                    const target = e.target as HTMLInputElement;
                    // Only allow numbers and dots
                    target.value = target.value.replace(/[^0-9.]/g, "");
                    // Prevent multiple dots
                    const parts = target.value.split(".");
                    if (parts.length > 2) {
                      target.value = parts[0] + "." + parts.slice(1).join("");
                    }
                    // Limit to 2 decimal places
                    if (parts.length === 2 && parts[1].length > 2) {
                      target.value = parts[0] + "." + parts[1].slice(0, 2);
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: colors.card.background,
                      borderRadius: "8px",
                      "& fieldset": { borderColor: colors.border.accent },
                      "&:hover fieldset": {
                        borderColor: colors.border.strong,
                      },
                      "&.Mui-focused fieldset": { borderColor: colors.border.focus },
                      "& input": { color: colors.isDark ? "#FFFFFF" : colors.text.primary, fontSize: "14px" },
                    },
                    "& .MuiFormHelperText-root": {
                      color: colors.accent.danger,
                      fontSize: "12px",
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text.secondary,
                    fontSize: "11px",
                    mt: 0.5,
                    display: "block",
                  }}
                >
                  Minimum: MYR 1.00
                </Typography>
              </Box>

              {/* Remarks Field */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text.secondary,
                    fontSize: "12px",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    mb: 1,
                    display: "block",
                  }}
                >
                  Remarks (Optional)
                </Typography>
                <TextField
                  fullWidth
                  {...remarks}
                  placeholder="Add a note for this payment..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: colors.card.background,
                      borderRadius: "8px",
                      "& fieldset": { borderColor: colors.border.accent },
                      "&:hover fieldset": {
                        borderColor: colors.border.strong,
                      },
                      "&.Mui-focused fieldset": { borderColor: colors.border.focus },
                      "& input": { color: colors.isDark ? "#FFFFFF" : colors.text.primary, fontSize: "14px" },
                    },
                    "& .MuiFormHelperText-root": {
                      color: colors.accent.danger,
                      fontSize: "12px",
                    },
                  }}
                />
              </Box>

              {/* Payment Method */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text.secondary,
                    fontSize: "12px",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    mb: 1,
                    display: "block",
                  }}
                >
                  Payment Method
                </Typography>
                <FormControl fullWidth error={!!errors.paymentMethod}>
                  <Controller
                    name="paymentMethod"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup {...field}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                          }}
                        >
                          <FormControlLabel
                            value="bank_transfer"
                            control={
                              <Radio
                                sx={{
                                  color: colors.border.strong,
                                  "&.Mui-checked": { color: colors.border.focus },
                                }}
                              />
                            }
                            label={
                              <Box sx={{ display: "flex", alignItems: "center" }}>
                                <AccountBalanceIcon sx={{ fontSize: "20px", mr: 1, color: colors.accent.gold }} />
                                <Typography sx={{ color: colors.isDark ? "#FFFFFF" : colors.text.primary, fontSize: "14px" }}>
                                  Bank Transfer
                                </Typography>
                              </Box>
                            }
                            sx={{
                              backgroundColor: watchedValues.paymentMethod === "bank_transfer"
                                ? colors.tint.primaryStrong
                                : colors.card.background,
                              borderRadius: "8px",
                              border: watchedValues.paymentMethod === "bank_transfer"
                                ? `1px solid ${colors.border.focus}`
                                : `1px solid ${colors.border.accent}`,
                              m: 0,
                              p: 1,
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor: colors.tint.primary,
                              },
                            }}
                          />
                          <FormControlLabel
                            value="duitnow"
                            control={
                              <Radio
                                sx={{
                                  color: colors.border.strong,
                                  "&.Mui-checked": { color: colors.accent.gold },
                                }}
                              />
                            }
                            label={
                              <Box sx={{ display: "flex", alignItems: "center" }}>
                                <QrCodeIcon sx={{ fontSize: "20px", mr: 1, color: colors.accent.gold }} />
                                <Typography sx={{ color: colors.isDark ? "#FFFFFF" : colors.text.primary, fontSize: "14px" }}>
                                  DuitNow
                                </Typography>
                              </Box>
                            }
                            sx={{
                              backgroundColor: watchedValues.paymentMethod === "duitnow"
                                ? colors.tint.primaryStrong
                                : colors.card.background,
                              borderRadius: "8px",
                              border: watchedValues.paymentMethod === "duitnow"
                                ? `1px solid ${colors.accent.gold}`
                                : `1px solid ${colors.border.accent}`,
                              m: 0,
                              p: 1,
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor: colors.tint.gold,
                              },
                            }}
                          />
                        </Box>
                      </RadioGroup>
                    )}
                  />
                </FormControl>
                {errors.paymentMethod && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.accent.danger,
                      fontSize: "12px",
                      mt: 0.5,
                      display: "block",
                    }}
                  >
                    {errors.paymentMethod.message}
                  </Typography>
                )}
              </Box>
              <Button
                fullWidth
                onClick={handleSubmit(onSubmit)}
                disabled={isLoading}
                sx={{
                  mt: 2,
                  py: 2,
                }}
              >
                {isLoading ? (
                  <CircularProgress size={20} sx={{ color: "#FFFFFF" }} />
                ) : (
                  "Continue to Payment"
                )}
              </Button>
            </Stack>

            {/* Navigation Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                mt: 4,
                pt: 3,
                borderTop: `1px solid ${colors.tint.goldStrong}`,
              }}
            >
              {/* History Button */}
              <Button
                onClick={() => navigate("/history?tab=bets")}
                startIcon={<HistoryIcon />}
                sx={{
                  color: colors.text.secondary,
                  fontSize: "11px",
                  textTransform: "none",
                  minWidth: "auto",
                  background: "transparent",
                  boxShadow: "none",
                  px: 2,
                  py: 0.5,
                  "&:hover": {
                    color: colors.accent.gold,
                    background: "transparent",
                    boxShadow: "none",
                  },
                }}
              >
                History
              </Button>

              {/* Logout Button */}
              {user && (
                <Button
                  onClick={handleLogout}
                  startIcon={<LogoutIcon />}
                  sx={{
                    color: colors.text.secondary,
                    fontSize: "11px",
                    textTransform: "none",
                    minWidth: "auto",
                    background: "transparent",
                    boxShadow: "none",
                    px: 2,
                    py: 0.5,
                    "&:hover": {
                      color: colors.accent.gold,
                      background: "transparent",
                      boxShadow: "none",
                    },
                  }}
                >
                  Logout
                </Button>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 4,
                mt: 3,
                pt: 3,
                borderTop: `1px solid ${colors.tint.goldStrong}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LockIcon sx={{ color: colors.text.secondary, fontSize: "16px" }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text.secondary,
                    fontSize: "11px",
                  }}
                >
                  SSL Secured
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SecurityIcon sx={{ color: colors.text.secondary, fontSize: "16px" }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text.secondary,
                    fontSize: "11px",
                  }}
                >
                  Secure Login
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Confirmation Modal */}
      {renderConfirmationModal()}

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />

      <LoadingOverlay open={isLoading} message="Processing payment..." />
    </LottoShell>
  );
};

export default PaymentPage;
